import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Button,
  ImageList,
  ImageListItem,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  Home,
  LocationOn,
  AttachMoney,
  Phone,
  Email,
  CalendarToday,
  ExpandMore,
  ArrowBack,
  Image as ImageIcon,
  Info,
  Build,
  LocalParking,
  Wifi,
  DirectionsCar,
  Elevator as ElevatorIcon,
  Link as LinkIcon,
  OpenInNew
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL } from './constants';
import axios from 'axios';

const RealEstateDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedListing, setSelectedListing] = useState(null);

  useEffect(() => {
    fetchDetailedData();
  }, [id]);

  const fetchDetailedData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch all data and find the one matching the ID
      const response = await axios.get(`${API_URL}/real_estate/sreality/detailed-data`, {
        params: {
          limit: 1000, // Get enough data to find the listing
          offset: 0
        }
      });

      if (response.data && response.data.data) {
        setColumns(response.data.columns || []);
        const listings = response.data.data;
        
        // Find the listing by ID or hash_id
        const idIndex = response.data.columns.indexOf('id');
        const hashIdIndex = response.data.columns.indexOf('hash_id');
        
        const listing = listings.find(row => {
          if (idIndex >= 0 && row[idIndex] && String(row[idIndex]) === String(id)) {
            return true;
          }
          if (hashIdIndex >= 0 && row[hashIdIndex] && String(row[hashIdIndex]) === String(id)) {
            return true;
          }
          return false;
        });
        
        if (listing) {
          setSelectedListing(listing);
        } else {
          setError(`Listing with ID ${id} not found`);
        }
      }
    } catch (err) {
      console.error('Error fetching detailed data:', err);
      setError('Failed to load listing details. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getValue = (columnName) => {
    if (!selectedListing || !columns.length) return null;
    const index = columns.indexOf(columnName);
    if (index >= 0 && index < selectedListing.length) {
      return selectedListing[index];
    }
    return null;
  };

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return String(price);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  const parseImageUrls = (urlString) => {
    if (!urlString) return [];
    try {
      // The URLs are stored as a JSON-like string array
      if (urlString.startsWith('[') && urlString.endsWith(']')) {
        // Remove brackets and split by comma, but be careful with URLs that contain commas
        const cleaned = urlString.slice(1, -1);
        // Split by ', ' (comma space) which seems to be the separator
        const urls = cleaned.split(', ').map(url => url.trim().replace(/^"|"$/g, ''));
        return urls.filter(url => url && url.startsWith('http'));
      }
      return [];
    } catch (e) {
      return [];
    }
  };

  const getImageUrls = () => {
    const allPictures = getValue('all_pictures_urls_list');
    const mainPicture = getValue('main_picture');
    const images = [];
    
    if (mainPicture) {
      images.push(mainPicture);
    }
    
    if (allPictures) {
      const parsed = parseImageUrls(allPictures);
      images.push(...parsed.filter(url => url !== mainPicture));
    }
    
    return images;
  };

  const renderField = (label, value, icon = null, formatFn = null) => {
    if (value === null || value === undefined || value === '') return null;
    
    const displayValue = formatFn ? formatFn(value) : String(value);
    
    return (
      <Grid item xs={12} sm={6} md={4}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
          {icon && <Box sx={{ mt: 0.5 }}>{icon}</Box>}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
              {displayValue}
            </Typography>
          </Box>
        </Box>
      </Grid>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !selectedListing) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back to Listings
        </Button>
        <Alert severity="error">{error || 'Listing not found'}</Alert>
      </Container>
    );
  }

  const images = getImageUrls();
  const address = getValue('address');
  const title = getValue('title') || getValue('description')?.substring(0, 100) || 'Real Estate Listing';
  const price = getValue('price');
  const description = getValue('description');

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        sx={{ mb: 3 }}
      >
        Back to Listings
      </Button>

      {/* Header Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
            <Home />
            {title}
          </Typography>
          
          {address && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <LocationOn color="action" />
              <Typography variant="h6" color="text.secondary">
                {address}
              </Typography>
            </Box>
          )}

          {price && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <AttachMoney color="success" sx={{ fontSize: 32 }} />
              <Typography variant="h3" component="div" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                {formatPrice(price)}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
            {getValue('status') && (
              <Chip label={getValue('status')} color="primary" />
            )}
            {getValue('object_type') && (
              <Chip label={getValue('object_type')} variant="outlined" />
            )}
            {getValue('object_kind') && (
              <Chip label={getValue('object_kind')} variant="outlined" />
            )}
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Images Section */}
        {images.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <ImageIcon />
                  Images ({images.length})
                </Typography>
                <ImageList cols={3} rowHeight={200} gap={8}>
                  {images.map((img, index) => (
                    <ImageListItem key={index}>
                      <img
                        src={img}
                        alt={`Property image ${index + 1}`}
                        loading="lazy"
                        style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </ImageListItem>
                  ))}
                </ImageList>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Description */}
        {description && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Info />
                  Description
                </Typography>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {renderField('Room Count', getValue('room_count_cb'), null)}
                {renderField('Floor Area', getValue('floor_area') ? `${getValue('floor_area')} m²` : null, null)}
                {renderField('Usable Area', getValue('usable_area') ? `${getValue('usable_area')} m²` : null, null)}
                {renderField('Estate Area', getValue('estate_area') ? `${getValue('estate_area')} m²` : null, null)}
                {renderField('Floor Number', getValue('floor_number'), null)}
                {renderField('Total Floors', getValue('floors'), null)}
                {renderField('Building Type', getValue('building_type'), null)}
                {renderField('Building Condition', getValue('building_condition'), null)}
                {renderField('Object Age', getValue('object_age'), null)}
                {renderField('Reconstruction Year', getValue('reconstruction_year'), null)}
                {renderField('Acceptance Year', getValue('acceptance_year'), null)}
                {renderField('Flat Class', getValue('flat_class'), null)}
                {renderField('Ownership', getValue('ownership'), null)}
                {renderField('Furnished', getValue('furnished'), null)}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Features & Amenities */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Features & Amenities
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {renderField('Balcony', getValue('balcony'), null)}
                {renderField('Balcony Area', getValue('balcony_area') ? `${getValue('balcony_area')} m²` : null, null)}
                {renderField('Loggia', getValue('loggia'), null)}
                {renderField('Loggia Area', getValue('loggia_area') ? `${getValue('loggia_area')} m²` : null, null)}
                {renderField('Terrace', getValue('terrace'), null)}
                {renderField('Terrace Area', getValue('terrace_area') ? `${getValue('terrace_area')} m²` : null, null)}
                {renderField('Garden Area', getValue('garden_area') ? `${getValue('garden_area')} m²` : null, null)}
                {renderField('Cellar', getValue('cellar'), null)}
                {renderField('Cellar Area', getValue('cellar_area') ? `${getValue('cellar_area')} m²` : null, null)}
                {renderField('Basin', getValue('basin'), null)}
                {renderField('Basin Area', getValue('basin_area') ? `${getValue('basin_area')} m²` : null, null)}
                {renderField('Elevator', getValue('elevator'), <ElevatorIcon fontSize="small" />)}
                {renderField('Parking', getValue('parking'), <LocalParking fontSize="small" />)}
                {renderField('Parking Lots', getValue('parking_lots'), null)}
                {renderField('Garage', getValue('garage'), <DirectionsCar fontSize="small" />)}
                {renderField('Garage Count', getValue('garage_count'), null)}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Technical Details */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Technical Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {renderField('Energy Efficiency Rating', getValue('energy_efficiency_rating'), null)}
                {renderField('Energy Performance Certificate', getValue('energy_performance_certificate'), null)}
                {renderField('Energy Performance Summary', getValue('energy_performance_summary'), null)}
                {renderField('Low Energy', getValue('low_energy'), null)}
                {renderField('Heating', getValue('heating_set_0'), null)}
                {renderField('Heating Source', getValue('heating_source_set_0'), null)}
                {renderField('Heating Element', getValue('heating_element_set_0'), null)}
                {renderField('Water', getValue('water_set_0'), null)}
                {renderField('Water Heat Source', getValue('water_heat_source_set_0'), null)}
                {renderField('Gas', getValue('gas_set_0'), null)}
                {renderField('Electricity', getValue('electricity_set_0'), null)}
                {renderField('Electricity (230V)', getValue('electricity_set_1'), null)}
                {renderField('Internet Connection', getValue('internet_connection_type_set_0'), <Wifi fontSize="small" />)}
                {renderField('Internet Provider', getValue('internet_connection_provider'), null)}
                {renderField('Road Type', getValue('road_type_set_0'), null)}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Financial & Legal */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Financial & Legal
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {renderField('Price', getValue('price'), <AttachMoney fontSize="small" />, formatPrice)}
                {renderField('Price Note', getValue('price_note'), null)}
                {renderField('Price Flag Negotiation', getValue('price_flag_negotiation_cb'), null)}
                {renderField('Commission', getValue('commission'), null)}
                {renderField('Cost of Living', getValue('cost_of_living'), null)}
                {renderField('Refundable Deposit', getValue('refundable_deposit'), null)}
                {renderField('Lease Type', getValue('lease_type'), null)}
                {renderField('Tenant Not Pay Commission', getValue('tenant_not_pay_commission'), null)}
                {renderField('Protection', getValue('protection'), null)}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Dates & Timeline */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarToday />
                Dates & Timeline
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {renderField('Beginning Date', getValue('beginning_date'), null, formatDate)}
                {renderField('Finish Date', getValue('finish_date'), null, formatDate)}
                {renderField('Ready Date', getValue('ready_date'), null, formatDate)}
                {renderField('First Tour Date', getValue('first_tour_date'), null, formatDate)}
                {renderField('Since', getValue('since'), null, formatDate)}
                {renderField('Updated At', getValue('updated_at'), null, formatDate)}
                {renderField('Edited', getValue('edited'), null, formatDate)}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Seller Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Seller Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {getValue('seller_name') && (
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      {getValue('seller_image') && (
                        <img
                          src={getValue('seller_image')}
                          alt="Seller"
                          style={{ width: 60, height: 60, borderRadius: '50%', objectFit: 'cover' }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      )}
                      <Box>
                        <Typography variant="h6">{getValue('seller_name')}</Typography>
                        {getValue('seller_rating') && (
                          <Typography variant="body2" color="text.secondary">
                            Rating: {getValue('seller_rating')}
                            {getValue('seller_review_count') && ` (${getValue('seller_review_count')} reviews)`}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                )}
                {renderField('Phone 1', getValue('phone_1'), <Phone fontSize="small" />)}
                {renderField('Phone 2', getValue('phone_2'), <Phone fontSize="small" />)}
                {renderField('Email', getValue('seller_email'), <Email fontSize="small" />)}
                {renderField('Seller ID', getValue('seller_id'), null)}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Additional Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Additional Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                {renderField('Advert Code', getValue('advert_code'), null)}
                {renderField('Hash ID', getValue('hash_id'), null)}
                {(() => {
                  // Try to find URL field - could be url, listing_url, sreality_url, etc.
                  const urlFields = ['url', 'listing_url', 'sreality_url', 'property_url', 'advert_url'];
                  for (const field of urlFields) {
                    const urlValue = getValue(field);
                    if (urlValue && (urlValue.startsWith('http://') || urlValue.startsWith('https://'))) {
                      return (
                        <Grid item xs={12} sm={6} md={4}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                            <Box sx={{ mt: 0.5 }}><LinkIcon fontSize="small" /></Box>
                            <Box>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Listing URL
                              </Typography>
                              <Typography 
                                variant="body2" 
                                component="a"
                                href={urlValue}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ 
                                  color: 'primary.main',
                                  textDecoration: 'none',
                                  '&:hover': { textDecoration: 'underline' },
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5
                                }}
                              >
                                {urlValue}
                                <OpenInNew fontSize="small" />
                              </Typography>
                            </Box>
                          </Box>
                        </Grid>
                      );
                    }
                  }
                  return null;
                })()}
                {renderField('Object Location', getValue('object_location'), null)}
                {renderField('Surroundings Type', getValue('surroundings_type'), null)}
                {renderField('Easy Access', getValue('easy_access'), null)}
                {renderField('Personal', getValue('personal'), null)}
                {renderField('State', getValue('state_cb'), null)}
                {renderField('Phase Distributions', getValue('phase_distributions_cb'), null)}
                {renderField('Circuit Breaker', getValue('circuit_breaker_cb'), null)}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default RealEstateDetailView;

